import ServicePage from "../components/ServicePage";
import heroPortrait from "../assets/heroes/hero-portrait.webp";
import heroFamily from "../assets/heroes/hero-family.webp";
import heroStudio from "../assets/heroes/hero-studio.webp";
import ctaPortrait from "../assets/heroes/cta-portrait.webp";
import ctaFamily from "../assets/heroes/cta-family.webp";
import ctaStudio from "../assets/heroes/cta-studio.webp";
import { PAGES, ADDRESS } from "../seoContent";


const HOW_BOOKING_WORKS = [
  {
    title: "Choose your session",
    text: "Pick a session length and a date and time that suit you on our [online booking page](/book).",
  },
  {
    title: "Visit our studio",
    text: `Come to our Rochdale studio at ${ADDRESS}. We guide you through posing and lighting so you feel relaxed.`,
  },
  {
    title: "Receive your photographs",
    text: "Selected photographs are professionally edited to a polished, natural finish.",
  },
];

export function PortraitPhotography() {
  const page = PAGES["/portrait-photographer-rochdale"];

  return (
    <ServicePage
      path="/portrait-photographer-rochdale"
      seoTitle={page.title}
      seoDescription={page.description}
      serviceName={page.serviceName}
      heroTitle={page.h1}
      heroImage={heroPortrait}
      ctaImage={ctaPortrait}
      heroPosition="center top"
      intro={page.intro}
      sections={[
        {
          heading: "A relaxed, guided portrait session",
          body: [
            "Great portraits come from feeling at ease. We take time to understand what you want from your session so the final photographs reflect your personality.",
            "Our dedicated studio gives you a comfortable space, professional lighting and plenty of guidance on posing.",
          ],
        },
        {
          heading: "Portraits for every purpose",
          body: [
            "Individual portraits, personal branding photographs, graduation pictures, or simply beautiful images to celebrate yourself. Ready to go? [Book a portrait session](/book).",
            "Tell us what the photographs are for and we will shape the session around it.",
          ],
        },
      ]}
      stepsTitle="Booking your portrait session"
      steps={HOW_BOOKING_WORKS}
      galleryTitle="Recent portrait sessions"
      gallery={[
        { n: 9, alt: "Professional studio portrait of a woman in a black blazer at Kinews Studio Rochdale" },
        { n: 3, alt: "Studio portrait of a smiling woman wearing glasses at Kinews Studio Rochdale" },
        { n: 7, alt: "Portrait of a woman in a hat against a teal backdrop at Kinews Studio Rochdale" },
        { n: 18, alt: "Studio portrait of a woman in a purple headscarf and sunglasses at Kinews Studio Rochdale" },
        { n: 25, alt: "Portrait of a woman in a traditional gele headwrap at Kinews Studio Rochdale" },
        { n: 38, alt: "Studio portrait of a woman in a magenta top at Kinews Studio Rochdale" },
      ]}
      faqs={page.faqs}
      cta={{
        heading: "Book your portrait session in Rochdale",
        text: "Choose a time that suits you and we will take care of the rest.",
        label: "Book a Portrait Session",
        to: "/book",
      }}
      related={[
        { to: "/family-photographer-rochdale", label: "Family photography in Rochdale" },
        { to: "/photography-studio-rochdale", label: "Our Rochdale photography studio" },
        { to: "/gallery", label: "Photography gallery" },
      ]}
    />
  );
}

export function FamilyPhotography() {
  const page = PAGES["/family-photographer-rochdale"];

  return (
    <ServicePage
      path="/family-photographer-rochdale"
      seoTitle={page.title}
      seoDescription={page.description}
      serviceName={page.serviceName}
      heroTitle={page.h1}
      heroImage={heroFamily}
      ctaImage={ctaFamily}
      heroPosition="center top"
      intro={page.intro}
      sections={[
        {
          heading: "Natural, relaxed family portraits",
          body: [
            "We guide you gently so the photographs feel like you, not stiff poses. Expect real smiles, closeness and personality.",
            "Our studio gives your family space to settle in, with professional lighting and a calm atmosphere.",
          ],
        },
        {
          heading: "From small families to generations together",
          body: [
            "From a small family portrait to special generational photographs, we create images your family can treasure for years.",
            "Matching outfits, coordinated colours or everyday style: choose what feels right for your family, then [book your session](/book).",
          ],
        },
      ]}
      stepsTitle="Booking your family session"
      steps={HOW_BOOKING_WORKS}
      galleryTitle="Recent family sessions"
      gallery={[
        { n: 17, alt: "Family portrait of parents and children in casual outfits at Kinews Studio Rochdale" },
        { n: 27, alt: "Family portrait in coordinated pink and black outfits at Kinews Studio Rochdale" },
        { n: 39, alt: "Family portrait in matching sports shirts at Kinews Studio Rochdale" },
        { n: 40, alt: "Family portrait in pink outfits at Kinews Studio Rochdale" },
        { n: 41, alt: "Parents and two young children in a family portrait at Kinews Studio Rochdale" },
        { n: 36, alt: "Family seated together on a rug during a family session at Kinews Studio Rochdale" },
      ]}
      faqs={page.faqs}
      cta={{
        heading: "Book a family photography session in Rochdale",
        text: "Pick a date that suits your family and we will create photographs you will love.",
        label: "Book a Family Session",
        to: "/book",
      }}
      related={[
        { to: "/portrait-photographer-rochdale", label: "Portrait photography in Rochdale" },
        { to: "/photography-studio-rochdale", label: "Our Rochdale photography studio" },
        { to: "/gallery", label: "Photography gallery" },
      ]}
    />
  );
}

export function StudioPhotography() {
  const page = PAGES["/photography-studio-rochdale"];

  return (
    <ServicePage
      path="/photography-studio-rochdale"
      seoTitle={page.title}
      seoDescription={page.description}
      serviceName={page.serviceName}
      heroTitle={page.h1}
      heroImage={heroStudio}
      ctaImage={ctaStudio}
      heroPosition="center top"
      intro={page.intro}
      sections={[
        {
          heading: "A professional Rochdale studio",
          body: [
            `Our studio is based at ${ADDRESS}, with a dedicated space for professional photography sessions.`,
            "Open Monday to Saturday, 9am to 6pm. [Book a studio session](/book) or [hire the studio](/rentals) for your own shoot.",
          ],
        },
        {
          heading: "Photography tailored to you",
          body: [
            "Every client and occasion is different, so we tailor each session to your requirements, from relaxed family portraits to maternity and personal shoots.",
            "Selected photographs are professionally edited for a polished finish that still looks natural.",
          ],
        },
      ]}
      stepsTitle="Booking a studio session"
      steps={HOW_BOOKING_WORKS}
      galleryTitle="Sessions at our studio"
      gallery={[
        { n: 1, alt: "Couple portrait session at Kinews Studio Rochdale" },
        { n: 12, alt: "Maternity photography session at Kinews Studio Rochdale" },
        { n: 22, alt: "Maternity portrait at Kinews Studio Rochdale" },
        { n: 10, alt: "Two women smiling together during a studio session at Kinews Studio Rochdale" },
        { n: 15, alt: "Studio portrait of a boy in a suit against a green backdrop at Kinews Studio Rochdale" },
        { n: 33, alt: "Relaxed studio portrait of a woman seated on a fur rug at Kinews Studio Rochdale" },
      ]}
      faqs={page.faqs}
      cta={{
        heading: "Book your studio session in Rochdale",
        text: "Choose a session and a time that suits you, or hire the studio for your own shoot.",
        label: "Book a Studio Session",
        to: "/book",
      }}
      related={[
        { to: "/rentals", label: "Rent our studio" },
        { to: "/portrait-photographer-rochdale", label: "Portrait photography in Rochdale" },
        { to: "/family-photographer-rochdale", label: "Family photography in Rochdale" },
      ]}
    />
  );
}
